import { jsx as _jsx } from "react/jsx-runtime";
import { AssigneeCard, lookupAssigneeCardRecord } from "../assignee-card/assignee-card";
/**
 * The chat's card vocabulary, and the one decision every surface that draws a
 * card makes: is the record's list still loading, did it fail, is the record
 * gone, or is there a card.
 *
 * Two surfaces read it — the hover card on an `entity://` mention chip
 * (`EntityMentionHoverCard`) and the history-only `entityCard` block
 * (`ChatEntityView`) — and they must agree on WHICH of the four a record is in.
 * How each draws it is its own: a popover and a block card carry different
 * frames and different placeholders. The one wording they share is
 * `chat.entityCard.unavailable`, because the thing that is gone is the record,
 * not either surface's view of it.
 */ /**
 * The chat's card vocabulary in the assignee model every card surface is keyed
 * on, plus the lookup list each one resolves in.
 *
 * Both are declared here because the readiness gate and the lookup read them
 * together: two hand-written copies could name different lists, which would
 * gate on one and read another. `team` maps to `OrgFunction`/`orgFunctions`
 * because the chat's `team` vocabulary covers any OrgFunction — both kinds
 * share one collection and one card.
 */ const CARD_MODELS = {
    user: "User",
    system: "System",
    team: "OrgFunction",
    externalContact: "ExternalContact"
};
const CARD_SOURCES = {
    user: "users",
    system: "systems",
    team: "orgFunctions",
    externalContact: "externalContacts"
};
/**
 * The informative line each card carries. One card, two kinds, two lines: a team
 * has a shared mailbox, a role (Stelle) usually does not, and the card renders
 * no second line at all for an empty field — which would silently degrade a role
 * to `name-only`, the outcome choosing a variant exists to avoid.
 */ const TRANSCRIPT_VARIANTS = {
    User: "with-role",
    System: "with-category",
    OrgFunction: (orgFunction)=>orgFunction.kind === "role" ? "with-description" : "with-email",
    ExternalContact: "with-email"
};
/**
 * Whether a mentioned entity type is one this package ships a card for.
 * `process`, `department` and `artifact` have none, so their chips can only ever
 * be links. Derived from `CARD_SOURCES` rather than hand-listed, so the set that
 * decides who gets a card cannot drift from the set that knows where to find it.
 */ export function isCardEntityType(type) {
    return type in CARD_SOURCES;
}
/**
 * The card for one entity, drawn by the same component the pickers, the
 * swimlanes and the detail pages use — dispatched by `AssigneeCard`, which every
 * other assignee surface shares.
 *
 * `null` means the id is not in the map, which is what a hard delete looks like
 * from the browser. The tombstone treatment is the cards' own: each reads
 * `deletedAt` and applies `DELETED_ENTITY_CLASS` + `appendDeletedSuffix`
 * itself, so a soft-deleted entity renders exactly as it does everywhere else.
 */ function entityCardFor(entityType, entityId, lookup, cardClassName) {
    const record = lookupAssigneeCardRecord(CARD_MODELS[entityType], entityId, lookup);
    return record ? /*#__PURE__*/ _jsx(AssigneeCard, {
        record: record,
        variants: TRANSCRIPT_VARIANTS,
        className: cardClassName
    }) : null;
}
/**
 * Resolve one person, system, team or external contact out of the assignee
 * lookup.
 *
 * The source is the assignee lookup rather than the per-entity detail hooks: it
 * already holds the `Populated*` shapes the cards take, and the four lists are
 * the same org-wide cached queries every picker subscribes to — no new
 * per-entity endpoint, and React Query deduplicates the subscriptions.
 *
 * Gated on the ONE list this card resolves in, not on all four: waiting on the
 * conjunction would hold the placeholder until the slowest org-wide list
 * returned, three of them for maps it never reads. A list that ERRORED is its
 * own state with its own retry: `retry: false` on the query client means one
 * failed request settles the list, and reading the empty map it leaves behind
 * would announce a deletion that never happened.
 *
 * CALL THIS WHERE THE CARD IS DRAWN, never where its trigger is. The four
 * queries subscribe wherever this hook runs, so a call in a mention chip would
 * make a transcript pull four org-wide lists because it merely CONTAINS a
 * mention. Both of today's hosts happen to hold those lists already — the
 * Omniscient app prefetches them, and the step recording page, Bliksy's only
 * mount point, subscribes to all four at page level — but that is a property of
 * the HOSTS, and the chat may not depend on it.
 */ export function useEntityCard(data, entityType, entityId, cardClassName) {
    const { lookup, stateBySource } = data.useAssigneeLookupWithReadiness();
    const source = stateBySource[CARD_SOURCES[entityType]];
    if (source.status !== "ready") return source;
    const card = entityCardFor(entityType, entityId, lookup, cardClassName);
    // Absent from a list that was fetched successfully and WITH `includeDeleted`
    // means the record is really gone — hard-deleted, or never this reader's to
    // see. There is no 404 to handle; the fetch having failed is the state above.
    return card ? {
        status: "ready",
        card
    } : {
        status: "unavailable"
    };
}

//# sourceMappingURL=use-entity-card.js.map